import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Instagram, ArrowRight, Star, ShieldCheck, Sparkles, Award, MapPin, Quote } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const Embers = () => {
    const ems = Array.from({ length: 28 });
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {ems.map((_, i) => (
                <span
                    key={i}
                    className="ember"
                    style={{
                        left: `${Math.random() * 100}%`,
                        bottom: `-${Math.random() * 20}px`,
                        animationDuration: `${10 + Math.random() * 16}s`,
                        animationDelay: `${Math.random() * 8}s`,
                        opacity: 0.4 + Math.random() * 0.5,
                    }}
                />
            ))}
        </div>
    );
};

const Stat = ({ value, label, suffix = "+" }) => (
    <div className="text-center">
        <div className="font-serif text-4xl md:text-5xl gold-text-gradient font-bold">{value}{suffix}</div>
        <div className="text-xs md:text-sm uppercase tracking-[0.2em] text-white/60 mt-2">{label}</div>
    </div>
);

const Home = () => {
    const { settings } = useSettings();
    const [services, setServices] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        api.get("/services").then(r => setServices(r.data));
        api.get("/gallery").then(r => setGallery(r.data));
        api.get("/reviews").then(r => setReviews(r.data));
    }, []);

    if (!settings) return <div className="min-h-screen grid place-items-center text-gold">Loading…</div>;

    const phone = settings.phone;
    const wa = (settings.whatsapp || phone).replace(/[^\d]/g, "");

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[100svh] flex items-center overflow-hidden grain">
                <div className="absolute inset-0">
                    <img
                        src={settings.hero_image_url}
                        alt="Wedding fireworks"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 hero-overlay" />
                    <Embers />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
                    <div className="max-w-3xl">
                        <div className="reveal text-xs uppercase tracking-[0.4em] text-gold mb-6 flex items-center gap-3">
                            <span className="w-12 h-px bg-gold" /> {settings.tagline}
                        </div>
                        <h1 className="reveal delay-1 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                            {settings.hero_title.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="gold-text-gradient italic">{settings.hero_title.split(" ").slice(-1)}</span>
                        </h1>
                        <p className="reveal delay-2 mt-7 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
                            {settings.hero_subtitle}
                        </p>
                        <div className="reveal delay-3 mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/contact" data-testid="hero-quote-btn" className="btn-gold inline-flex items-center gap-2">
                                Get a Quote <ArrowRight size={18} />
                            </Link>
                            <a href={`tel:${phone}`} data-testid="hero-call-btn" className="btn-outline-gold inline-flex items-center gap-2">
                                <Phone size={16} /> Call Now
                            </a>
                            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-red inline-flex items-center gap-2">
                                <MessageCircle size={16} /> WhatsApp
                            </a>
                            <a href={settings.instagram_url} target="_blank" rel="noreferrer" data-testid="hero-instagram-btn" className="text-white/80 hover:text-gold inline-flex items-center gap-2 px-4 py-3">
                                <Instagram size={18} /> Instagram
                            </a>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-8 left-0 right-0 z-10">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 glass-card rounded-2xl py-6 px-6 md:px-10">
                        <Stat value={settings.stats_events} label="Events Produced" />
                        <Stat value={settings.stats_clients} label="Happy Clients" />
                        <Stat value={settings.stats_cities} label="Cities Served" />
                        <Stat value={settings.stats_years} label="Years Experience" />
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-bg-base">
                <div className="max-w-5xl mx-auto text-center">
                    <div className="text-xs uppercase tracking-[0.4em] text-gold mb-5">About Us</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-white mb-6 leading-tight">
                        Cinematic moments engineered with <span className="italic gold-text-gradient">precision</span>
                    </h2>
                    <p className="text-white/70 leading-relaxed text-base md:text-lg">{settings.about_text}</p>
                    <Link to="/about" data-testid="home-about-cta" className="inline-flex items-center gap-2 mt-8 text-gold hover:text-gold-hover transition-colors group">
                        Read our story <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </section>

            {/* Services Highlights */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-bg-surface border-y border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">What We Do</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">Signature <span className="italic gold-text-gradient">Effects</span></h2>
                        </div>
                        <Link to="/services" data-testid="home-services-cta" className="btn-outline-gold inline-flex items-center gap-2 self-start">
                            All Services <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.slice(0, 6).map((s) => (
                            <div
                                key={s.id}
                                data-testid={`service-card-${s.id}`}
                                className="zoom-on-hover group relative overflow-hidden rounded-xl border border-white/10 bg-bg-base hover:border-gold/40 transition-all duration-500"
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="font-serif text-xl md:text-2xl text-white mb-2">{s.title}</h3>
                                    <p className="text-sm text-white/70 line-clamp-2">{s.description}</p>
                                    <Link to="/contact" className="mt-4 inline-flex items-center gap-2 text-gold text-sm hover:text-gold-hover">
                                        Enquire <ArrowRight size={14} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-bg-base">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Why Choose Us</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white">The <span className="italic gold-text-gradient">Star Events</span> difference</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: ShieldCheck, t: "Certified Safety", d: "Trained pyrotechnicians, indoor-safe cold flame & venue-approved setups." },
                            { icon: Sparkles, t: "Premium Equipment", d: "Latest sparkulars, CO₂ jets and low-fog machines for cinematic results." },
                            { icon: Award, t: "1000+ Events", d: "A decade of weddings, sangeets, corporate galas and concerts delivered." },
                            { icon: MapPin, t: "Pan-India Reach", d: "Based in Nagpur, serving 35+ cities across Maharashtra and India." },
                        ].map((f, i) => (
                            <div key={i} className="glass-card rounded-xl p-7 hover:border-gold/50 transition-all duration-500 hover:-translate-y-1">
                                <f.icon className="text-gold mb-5" size={28} />
                                <h3 className="font-serif text-xl text-white mb-3">{f.t}</h3>
                                <p className="text-sm text-white/65 leading-relaxed">{f.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallery preview */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-bg-surface border-y border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Recent Work</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-white">From our <span className="italic gold-text-gradient">archive</span></h2>
                        </div>
                        <Link to="/gallery" data-testid="home-gallery-cta" className="btn-outline-gold inline-flex items-center gap-2 self-start">
                            View Gallery <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {gallery.slice(0, 8).map((g) => (
                            <Link key={g.id} to="/gallery" className="zoom-on-hover overflow-hidden rounded-lg group relative aspect-square">
                                <img src={g.image_url} alt={g.title} className="w-full h-full object-cover" loading="lazy" />
                                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reviews */}
            <section className="py-24 md:py-32 px-6 md:px-12 bg-bg-base">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Client Love</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white">Reviews from our <span className="italic gold-text-gradient">families</span></h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {reviews.slice(0, 6).map((r) => (
                            <div key={r.id} data-testid={`review-${r.id}`} className="glass-card rounded-xl p-7 relative">
                                <Quote className="absolute top-5 right-5 text-gold/30" size={28} />
                                <div className="flex items-center gap-1 mb-4">
                                    {Array.from({ length: r.rating }).map((_, i) => (
                                        <Star key={i} size={14} className="fill-gold text-gold" />
                                    ))}
                                </div>
                                <p className="text-white/75 text-sm leading-relaxed italic mb-6">"{r.review}"</p>
                                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                                    {r.photo_url && <img src={r.photo_url} alt={r.name} className="w-10 h-10 rounded-full object-cover" />}
                                    <div>
                                        <div className="text-white text-sm font-semibold">{r.name}</div>
                                        <div className="text-gold/80 text-xs uppercase tracking-wider">{r.event_type}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img src="https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=1600&q=80" alt="Stage" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
                </div>
                <div className="relative max-w-4xl mx-auto text-center">
                    <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight">
                        Ready to make your event <span className="italic gold-text-gradient">unforgettable?</span>
                    </h2>
                    <p className="text-white/75 mt-6 text-base md:text-lg">Let's design a cinematic moment your guests will never forget.</p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/contact" data-testid="cta-quote-btn" className="btn-gold inline-flex items-center gap-2">Get a Free Quote <ArrowRight size={18} /></Link>
                        <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-btn" className="btn-outline-gold inline-flex items-center gap-2"><MessageCircle size={16} /> Chat on WhatsApp</a>
                    </div>
                </div>
            </section>

            {/* Map + Contact */}
            <section className="py-24 px-6 md:px-12 bg-bg-surface border-t border-white/5">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Visit Us</div>
                        <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Our Nagpur Studio</h2>
                        <p className="text-white/70 leading-relaxed mb-6">{settings.address}</p>
                        <div className="space-y-3 text-sm">
                            <a href={`tel:${phone}`} className="flex items-center gap-3 text-white/80 hover:text-gold"><Phone size={16} className="text-gold" /> {phone}</a>
                            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/80 hover:text-gold"><MessageCircle size={16} className="text-gold" /> WhatsApp Us</a>
                            <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/80 hover:text-gold"><Instagram size={16} className="text-gold" /> @stareventnagpur</a>
                        </div>
                    </div>
                    <div className="rounded-xl overflow-hidden border border-white/10 min-h-[320px]">
                        <iframe
                            data-testid="home-map"
                            title="Star Events Location"
                            src={settings.google_maps_embed}
                            className="w-full h-full min-h-[320px]"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
